import type { BadgeColor } from '@civilgrid/ui/components';

type ResearchStatusColor = Extract<BadgeColor, 'grey' | 'blue' | 'green' | 'yellow'>;

/** Badge color per customer-facing research status label. */
const RESEARCH_STATUS_COLORS = {
  Completed: 'green',
  'No Response': 'yellow',
  'In Progress': 'blue',
  'Not Started': 'grey',
} as const satisfies Record<string, ResearchStatusColor>;

export type ResearchStatusLabel = keyof typeof RESEARCH_STATUS_COLORS;

export const RESEARCH_STATUS_LABELS = Object.keys(RESEARCH_STATUS_COLORS) as ResearchStatusLabel[];

/**
 * Customer-facing label for each raw `agencies.researchStatus` value (AGENCY_RESEARCH_STATUSES in
 * @civilgrid/db plus the 'Not Started' the API substitutes for null).
 *
 * Shared by the Data Refresh Status page and the project Intel tab so the surfaces never drift on
 * status vocabulary or colors.
 */
const LABEL_BY_RAW_STATUS: ReadonlyMap<string, ResearchStatusLabel> = new Map([
  ['Closed - No Data', 'Completed'],
  ['Published', 'Completed'],
  ['Agency Not Responsive', 'No Response'],
  ['Open - Outreach Complete', 'In Progress'],
  ['Open - Processing', 'In Progress'],
  ['Not Started', 'Not Started'],
  ['Open - Pending Outreach', 'Not Started'],
]);

const RAW_STATUSES_BY_LABEL = new Map<string, string[]>(RESEARCH_STATUS_LABELS.map((label) => [label, []]));
for (const [status, label] of LABEL_BY_RAW_STATUS) RAW_STATUSES_BY_LABEL.get(label)?.push(status);

/** Customer-facing label for a raw status; unknown statuses display as-is. */
export function getResearchStatusLabel(status: string): string {
  return LABEL_BY_RAW_STATUS.get(status) ?? status;
}

/** Unknown statuses still render, with a grey badge fallback. */
export function getResearchStatusVariant(status: string): ResearchStatusColor {
  const label = LABEL_BY_RAW_STATUS.get(status);
  return label ? RESEARCH_STATUS_COLORS[label] : 'grey';
}

/** Raw statuses behind a label; anything else is treated as a raw status already. */
export function getRawResearchStatuses(label: string): readonly string[] {
  return RAW_STATUSES_BY_LABEL.get(label) ?? [label];
}
